import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import LinkIcon from '@mui/icons-material/Link';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { useState } from 'react';

interface InviteResponse {
  code: string;
  inviteUrl: string;
  email: string;
  emailSent: boolean;
  expiresInDays: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  inviteData: InviteResponse | null;
}

export function InviteSuccessDialog({ open, onClose, inviteData }: Props) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!inviteData) return;

    try {
      await navigator.clipboard.writeText(inviteData.inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!inviteData) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Stack direction='row' alignItems='center' spacing={1}>
          <CheckIcon sx={{ color: 'success.main' }} />
          <Typography variant='h6'>
            {inviteData.emailSent ? 'Invitation Sent!' : 'Invitation Created!'}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Email status */}
          <Box>
            <Stack direction='row' alignItems='center' spacing={1} mb={1}>
              <EmailIcon fontSize='small' sx={{ color: 'text.secondary' }} />
              <Typography variant='subtitle2' color='text.secondary'>
                Email Status
              </Typography>
            </Stack>

            {inviteData.emailSent ? (
              <Alert severity='success' variant='outlined'>
                Invitation email sent to <strong>{inviteData.email}</strong>
              </Alert>
            ) : (
              <Alert
                severity='info'
                variant='outlined'
                sx={{
                  '& .MuiAlert-message': {
                    color: 'text.primary',
                  },
                }}
              >
                Email not sent. Share the link below with {inviteData.email}
              </Alert>
            )}
          </Box>

          {/* Invite link */}
          <Box>
            <Stack direction='row' alignItems='center' spacing={1} mb={1}>
              <LinkIcon fontSize='small' sx={{ color: 'text.secondary' }} />
              <Typography variant='subtitle2' color='text.secondary'>
                Invitation Link
              </Typography>
            </Stack>

            <TextField
              fullWidth
              value={inviteData.inviteUrl}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      onClick={handleCopy}
                      edge='end'
                      color={copied ? 'success' : 'default'}
                      aria-label={copied ? 'Copied' : 'Copy to clipboard'}
                    >
                      {copied ? <CheckIcon /> : <ContentCopyIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              helperText={`This invitation expires in ${inviteData.expiresInDays} days`}
            />
          </Box>

          {/* QR Code */}
          <Box>
            <Stack direction='row' alignItems='center' spacing={1} mb={2}>
              <QrCode2Icon fontSize='small' sx={{ color: 'text.secondary' }} />
              <Typography variant='subtitle2' color='text.secondary'>
                QR Code
              </Typography>
            </Stack>

            <Stack alignItems='center' spacing={2}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'white',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <QRCodeSVG
                  value={inviteData.inviteUrl}
                  size={150}
                  level='M'
                  includeMargin={false}
                />
              </Box>
              <Typography
                variant='caption'
                color='text.secondary'
                textAlign='center'
              >
                Scan to open signup page
              </Typography>
            </Stack>
          </Box>

          {/* Instructions */}
          <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
            <Stack spacing={1}>
              <Typography variant='body2' color='text.secondary'>
                {inviteData.emailSent
                  ? 'The recipient will receive an email with instructions to create their account. You can also share the link or QR code directly if needed.'
                  : 'Share this link or QR code with the recipient so they can create their account.'}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                The link can only be used once.
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant='contained'>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
